import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Package } from "lucide-react";

import { api } from "../lib/api";
import { gbp, num } from "../lib/format";

/* LT-36 — how much of a tender's materials money is still priced on Flat
 * Material Rates, per library item, biggest first — so the recipes worth
 * building next are obvious. Either `report` is handed in (Order list tab,
 * follows its filters) or the panel fetches the whole-tender figures. */
const readOpen = (key) => { try { return localStorage.getItem(key) === "1"; } catch { return false; } };

function ReportTable({ rep, showPrices }) {
  return (
    <table className="w-full mt-3" data-testid="flat-materials-table">
      <thead>
        <tr className="bg-surface-2">
          <th className="col-head text-left px-3 py-1.5">Library item</th>
          <th className="col-head text-right px-3 py-1.5" style={{ width: 70 }}>Lines</th>
          <th className="col-head text-right px-3 py-1.5" style={{ width: 110 }}>Qty</th>
          {showPrices && (
            <>
              <th className="col-head text-right px-3 py-1.5" style={{ width: 100 }}>£/unit</th>
              <th className="col-head text-right px-3 py-1.5" style={{ width: 110 }}>Materials £</th>
              <th className="col-head text-right px-3 py-1.5" style={{ width: 80 }}>Share</th>
            </>
          )}
          <th className="col-head text-right px-3 py-1.5" style={{ width: 120 }} />
        </tr>
      </thead>
      <tbody>
        {rep.items.map((it, i) => {
          const key = it.library_item_id || `row-${i}`;
          return (
            <tr key={key} className="border-t border-line-soft" data-testid={`flat-materials-row-${key}`}>
              <td className="px-3 py-1.5 text-ink" style={{ fontSize: 12 }}>
                {it.description}
                {it.note && <span className="text-ink-4" style={{ fontSize: 10.5 }}> · {it.note}</span>}
                {it.line_refs.length > 0 && <span className="block text-ink-4" style={{ fontSize: 10.5 }}>lines {it.line_refs.join(", ")}</span>}
              </td>
              <td className="px-3 py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{it.line_count}</td>
              <td className="px-3 py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{num(it.qty)} <span className="font-sans text-ink-4">{it.unit}</span></td>
              {showPrices && (
                <>
                  <td className="px-3 py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{it.rate != null ? gbp(it.rate) : "varies"}</td>
                  <td className="px-3 py-1.5 text-right font-mono tnum text-ink" style={{ fontSize: 12, fontWeight: 600 }} data-testid={`flat-materials-cost-${key}`}>{gbp(it.cost)}</td>
                  <td className="px-3 py-1.5 text-right font-mono tnum text-ink-3" style={{ fontSize: 12 }}>{num(it.share_pct, 0)}%</td>
                </>
              )}
              <td className="px-3 py-1.5 text-right">
                {it.library_item_id ? (
                  <a href={`/library/${it.library_item_id}?build=1`} target="_blank" rel="noreferrer"
                    className="text-accent hover:underline whitespace-nowrap" style={{ fontSize: 11.5, fontWeight: 500 }}
                    data-testid={`flat-materials-build-${key}`}>
                    Build recipe →
                  </a>
                ) : (
                  <span className="text-ink-4" style={{ fontSize: 11 }}>ad-hoc line</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function FlatMaterialsPanel({ tenderId, report, refreshKey, storageKey = "lt36_flat_open", filtered = false, showPrices = true }) {
  const [open, setOpen] = useState(() => readOpen(storageKey));
  const [fetched, setFetched] = useState(null);
  const rep = report || fetched;

  useEffect(() => {
    if (report || !tenderId || refreshKey === null) return undefined;
    let alive = true;
    api.get(`/tenders/${tenderId}/flat-material-report`)
      .then((r) => { if (alive) setFetched(r.data); })
      .catch(() => { if (alive) setFetched(null); });
    return () => { alive = false; };
  }, [tenderId, report, refreshKey]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
  };

  if (!rep || !rep.items || !rep.items.length) return null;
  const Chevron = open ? ChevronDown : ChevronRight;
  const priced = showPrices && rep.flat_cost != null;
  return (
    <div className="rounded-lg border border-[#EAD9A6] bg-surface" data-testid="flat-materials-panel">
      <button type="button" onClick={toggle} className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-[#F8F1DF]/60 transition-colors" data-testid="flat-materials-toggle" aria-expanded={open}>
        <span className="inline-flex items-center gap-2 text-ink" style={{ fontSize: 12.5 }}>
          <Package size={13} className="text-[#7A5A06]" />
          <span>
            {priced ? (
              <>
                <span className="font-mono tnum" style={{ fontWeight: 700 }} data-testid="flat-materials-summary-cost">{gbp(rep.flat_cost)}</span>
                {" of "}<span className="font-mono tnum">{gbp(rep.materials_cost)}</span>
                {" materials ("}<span className="font-mono tnum" data-testid="flat-materials-summary-pct">{num(rep.share_pct, 0)}%</span>{") still on flat rates"}
              </>
            ) : (
              <><span className="font-mono tnum" style={{ fontWeight: 700 }} data-testid="flat-materials-summary-lines">{rep.line_count}</span> line{rep.line_count === 1 ? "" : "s"} still on flat material rates</>
            )}
            {" · "}{rep.item_count} library item{rep.item_count === 1 ? "" : "s"}
            {filtered ? " in this selection" : ""}
          </span>
        </span>
        <span className="inline-flex items-center gap-1 text-ink-3" style={{ fontSize: 11.5 }}>
          {open ? "Hide" : "Which items"} <Chevron size={13} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 border-t border-line-soft">
          <ReportTable rep={rep} showPrices={priced} />
          <p className="text-ink-4 mt-2" style={{ fontSize: 10.5 }} data-testid="flat-materials-note">
            Biggest first — build these recipes to turn the money into buying quantities. Estimate maths (qty × £/unit); "Build recipe" opens the library item in build-out mode.
          </p>
        </div>
      )}
    </div>
  );
}
